import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Upload, ImageOff, Save } from "lucide-react";

export default function AdminSettings() {
  const { reloadSettings } = useSettings();
  const [s, setS] = useState(null);
  const logoRef = useRef();
  const heroRef = useRef();

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <p className="text-slate-400">Caricamento...</p>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    set(key, data.url); toast.success("Immagine caricata");
  };

  const save = async () => {
    await api.put("/admin/settings", s);
    await reloadSettings();
    toast.success("Impostazioni salvate");
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-slate-900">Impostazioni Negozio</h1>

      <Card className="mt-6 p-6 rounded-2xl border-slate-200">
        <h3 className="font-display font-bold text-slate-900 mb-4">Informazioni negozio</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center">{fileUrl(s.logo_url) ? <img src={fileUrl(s.logo_url)} alt="" className="w-full h-full object-contain" /> : <ImageOff className="w-5 h-5 text-emerald-200" />}</div>
          <input type="file" accept="image/*" ref={logoRef} onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo_url")} className="hidden" data-testid="logo-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => logoRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Logo</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">Nome negozio</Label><Input value={s.shop_name || ""} onChange={(e) => set("shop_name", e.target.value)} data-testid="set-shop-name" /></div>
          <div><Label className="mb-1.5 block">Valuta</Label><Input value={s.currency || "EUR"} onChange={(e) => set("currency", e.target.value)} /></div>
          <div><Label className="mb-1.5 block">Email</Label><Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="set-email" /></div>
          <div><Label className="mb-1.5 block">Telefono</Label><Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="set-phone" /></div>
          <div className="sm:col-span-2"><Label className="mb-1.5 block">Indirizzo</Label><Input value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        </div>
      </Card>

      <Card className="mt-6 p-6 rounded-2xl border-slate-200">
        <h3 className="font-display font-bold text-slate-900 mb-4">Home page</h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-24 h-16 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center">{fileUrl(s.hero_image) ? <img src={fileUrl(s.hero_image)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-5 h-5 text-emerald-200" />}</div>
          <input type="file" accept="image/*" ref={heroRef} onChange={(e) => e.target.files[0] && upload(e.target.files[0], "hero_image")} className="hidden" data-testid="hero-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => heroRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Banner</Button>
        </div>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block">Titolo banner</Label><Input value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="set-hero-title" /></div>
          <div><Label className="mb-1.5 block">Sottotitolo banner</Label><Textarea rows={2} value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} data-testid="set-hero-subtitle" /></div>
        </div>
      </Card>

      <Card className="mt-6 p-6 rounded-2xl border-slate-200">
        <h3 className="font-display font-bold text-slate-900 mb-4">Spedizione</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">Costo spedizione (€)</Label><Input type="number" step="0.01" value={s.shipping_cost} onChange={(e) => set("shipping_cost", e.target.value)} data-testid="set-shipping" /></div>
          <div><Label className="mb-1.5 block">Soglia spedizione gratuita (€)</Label><Input type="number" step="0.01" value={s.free_shipping_threshold} onChange={(e) => set("free_shipping_threshold", e.target.value)} data-testid="set-threshold" /></div>
          <div><Label className="mb-1.5 block">Giorni consegna (min)</Label><Input type="number" min="1" value={s.delivery_days_min ?? 2} onChange={(e) => set("delivery_days_min", e.target.value)} data-testid="set-delivery-min" /></div>
          <div><Label className="mb-1.5 block">Giorni consegna (max)</Label><Input type="number" min="1" value={s.delivery_days_max ?? 5} onChange={(e) => set("delivery_days_max", e.target.value)} data-testid="set-delivery-max" /></div>
        </div>
      </Card>

      <Card className="mt-6 p-6 rounded-2xl border-slate-200">
        <h3 className="font-display font-bold text-slate-900 mb-4">Testi legali</h3>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block">Privacy Policy</Label><Textarea rows={4} value={s.privacy_text || ""} onChange={(e) => set("privacy_text", e.target.value)} data-testid="set-privacy" /></div>
          <div><Label className="mb-1.5 block">Termini e Condizioni</Label><Textarea rows={4} value={s.terms_text || ""} onChange={(e) => set("terms_text", e.target.value)} data-testid="set-terms" /></div>
        </div>
      </Card>

      <div className="mt-6 sticky bottom-4"><Button onClick={save} className="rounded-full shadow-lg" data-testid="settings-save"><Save className="w-4 h-4 mr-1" /> Salva modifiche</Button></div>
    </div>
  );
}
